import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

class BuySharedItemTrackingProviderComponent extends Component {
    constructor() {
        super();

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
        this.attemptedItemImpressionIndexes = [];
    }

    componentDidMount() {
        /**
         * GA requirment - first batch of impressions should fire on page load.
         */
        this.fireItemImpressionTracking(0);
    }

    componentDidUpdate() {
        if (this.regionalInfoAvailable() && this.attemptedItemImpressionIndexes.length !== 0) {
            this.attemptedItemImpressionIndexes.forEach(attemptedItemImpressionIndex => {
                this.fireItemImpressionTracking(attemptedItemImpressionIndex);
            });
            this.attemptedItemImpressionIndexes = [];
        }
    }

    getRegions() {
        return (
            getUserSessionRegions() || this.props.viewer?.regionalInfo?.[0]?.regionsByZipCode || []
        );
    }

    regionalInfoAvailable() {
        return !!(getUserSessionRegions() || this.props.viewer?.regionalInfo);
    }

    trackProductImpression(startIndex) {
        const { buyPage } = this.props;
        const { pageType, edges: items } = buyPage || {};
        const userRegions = this.getRegions();

        trackEcommerceProductImpressions({
            items,
            startIndex,
            pageType,
            userRegions,
        });
    }

    fireItemImpressionTracking(index) {
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);
        if (!this.regionalInfoAvailable()) {
            this.attemptedItemImpressionIndexes.push(impressionGroup);
        } else if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            /**
             * Very important to track indexes that have already fired - `react-lazy-load` can fire
             * `contentVisible` handler multiple times for the same item.
             */
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const { buyPage } = this.props;
        const { pageType, edges: items } = buyPage || {};
        const itemNode = items?.[index]?.node;
        const item = itemNode?.item || itemNode;
        const userRegions = this.getRegions();

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            userRegions,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

BuySharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    buyPage: PropTypes.object.isRequired,
    viewer: PropTypes.object,
};

export const BuySharedItemTrackingProvider = createFragmentContainer(
    BuySharedItemTrackingProviderComponent,
    {
        buyPage: graphql`
            fragment BuySharedItemTrackingProvider_buyPage on ItemSearchQueryConnection {
                pageType
                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                type
                            }
                            ...freeShippingHelpers_item
                        }
                    }
                }
            }
        `,
        viewer: graphql`
            fragment BuySharedItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
